/* eslint-disable */
import * as Dialog from '@radix-ui/react-dialog';
import * as Tabs from '@radix-ui/react-tabs';
import Card from 'components/card';
import { useEffect, useState } from 'react';
import { MdClose } from 'react-icons/md';
import { User } from 'views/types/typeUser';
import { KybSection } from '../KybSection';
import { UserTxsTable } from '../UserTxsTable';
import { UserLogs } from '../UserLogs';
import { formatDate } from 'utils/date';
import { BlockConfirmDialog } from '../BlockConfirmDialog';
import { Button } from 'components/button';
import clsx from 'clsx';
import { formatNumber } from 'utils/formatNumber';
import { Link } from 'react-router-dom';
import UserCardsTab from '../UserCards/UserCards';
import UserReferrals from '../UserReferrals/UserReferrals';
import { usersService } from 'views/services/usersService';

import TransactionDetails from 'views/admin/Transactions/components/TransactionDetails/TransactionDetails';

import { IDepositExtended } from 'views/types/typeTransaction';
import { useUsersStore } from 'views/store/useUsersStore';
import { Disable2FaDialog } from '../Disable2FaDialog';
import { useTransactionFilterStore } from 'views/store/useUserTxsTableStore';
import { UserWallets } from '../UserWallets';
import { UserBalances } from '../UserBalances';
import useSWR from 'swr';
import { ReadPermissionGate } from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { usePermissions } from 'hooks/usePermissions';
import { adminLogsService } from 'views/services/adminLogsService';

interface IProps {
  children: React.ReactNode;
  onOpenChange: (isOpen: boolean) => void;
  refetchUsers: () => void;
  user: User;
  onUserClick?: (userId: string) => void;
}

const UserHistoryDialog: React.FC<IProps> = ({
  children,
  onOpenChange,
  user,
  refetchUsers,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<User>(user);
  const [tabValue, setTabValue] = useState('tab1');
  const { goBackReferral, currentReferral, referralStack, resetReferrals } =
    useUsersStore();
  const { hasPermission, canRead, canUpdate } = usePermissions();

  const { resetFilters } = useTransactionFilterStore();

  useEffect(() => {
    setCurrentUser(user);
  }, [user]);

  const [currentView, setCurrentView] = useState('userDetails');
  const [selectedTransaction, setSelectedTransaction] =
    useState<IDepositExtended | null>(null);

  const handleTransactionClick = (transaction: IDepositExtended) => {
    setSelectedTransaction(transaction);
    setCurrentView('transactionDetails');
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen && !hasPermission(PERMISSION_RESOURCES.CLIENT_PROFILE, 'read')) {
      return;
    }

    setIsOpen(isOpen);
    onOpenChange(isOpen);

    if (isOpen) {
      resetReferrals();
    } else {
      setCurrentUser(user);
      setTabValue('tab1');
    }
  };

  const getTransactionProps = (transaction: IDepositExtended | null) => {
    if (!transaction) {
      return {
        transaction: {} as IDepositExtended,
        isInternal: false,
        isSwap: false,
        isWithdraw: false,
        isCardTransaction: false,
        isReferralTransaction: false,
        showActions: false,
        isBlocked: false,
        onBack: () => setCurrentView('userDetails'),
      };
    }

    const type = transaction.type?.toLowerCase() || '';

    return {
      transaction: transaction,
      isInternal: type.includes('internal'),
      isSwap: type.includes('swap'),
      isWithdraw: type.includes('withdraw'),
      isCardTransaction: type.includes('card'),
      isReferralTransaction: type.includes('referral'),
      showActions: false,
      isBlocked: false,
      onBack: () => setCurrentView('userDetails'),
    };
  };

  useEffect(() => {
    resetFilters();
  }, [tabValue]);

  useEffect(() => {
    if (
      currentView === 'transactionDetails' &&
      !canRead(PERMISSION_RESOURCES.TRANSACTION_INNER_PAGE)
    ) {
      setCurrentView('userDetails');
      setSelectedTransaction(null);
    }
  }, [currentView, canRead]);

  const handleExportCsv = async () => {
    await usersService.exportLogsToCsv(currentUser.id);
  };

  const { data: refreshedUser, mutate: refreshUser } = useSWR(
    isOpen && tabValue === 'tab1' ? ['user', currentUser.id] : null,
    () => usersService.getUser(currentUser.id),
    { refreshInterval: 10000 }
  );

  useEffect(() => {
    if (refreshedUser) {
      setCurrentUser(refreshedUser);
    }
  }, [refreshedUser]);

  const { data: referralReward, isLoading: isRewardsLoading } = useSWR(
    currentUser.id && isOpen ? `user-referrals-${currentUser.id}` : null,
    () => usersService.getUserReferrals(currentUser.id),
    { refreshInterval: 10000 }
  );

  const handleKYCClick = async () => {
    try {
      await adminLogsService.logKycView(currentUser.coinsFlyId);
    } catch (error) {
      console.error('Failed to log KYC view:', error);
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange} modal>
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=open]:animate-overlayShow  fixed inset-0 z-50 bg-gray-800 bg-opacity-50" />
        <Dialog.Content
          onInteractOutside={e => {
            if ((e.target as HTMLElement).closest('.chakra-toast')) {
              e.preventDefault();
            }
          }}
          className="data-[state=open]:animate-contentShow fixed left-[50%] top-[50%] z-[60] max-h-[85vh] h-[85vh] w-[90vw] max-w-[90vw] translate-x-[-50%] translate-y-[-50%] rounded-[6px] bg-white lg-max:px-2 p-[25px] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none dark:bg-gray-800 dark:text-white"
        >
          <Dialog.Title className="sm-max:w-full sm-max:flex-col-reverse flex justify-center items-center relative text-center text-mauve12 mb-10 text-[17px] font-medium">
            <div className="absolute left-4 top-0 flex gap-2">
              <ReadPermissionGate resource={PERMISSION_RESOURCES.BLOCK_CTA}>
                <BlockConfirmDialog
                  user={currentUser}
                  refetchUsers={refetchUsers}
                >
                  <Button
                    disabled={
                      !canUpdate(PERMISSION_RESOURCES.CLIENTS_LIST) ||
                      !canUpdate(PERMISSION_RESOURCES.CLIENT_PROFILE) ||
                      !canUpdate(PERMISSION_RESOURCES.BLOCK_CTA)
                    }
                    className={clsx(
                      'justify-center',
                      currentUser.userStatus === 'active'
                        ? 'bg-red-600 hover:bg-red-700'
                        : 'bg-green-600 hover:bg-green-700'
                    )}
                  >
                    {currentUser.userStatus === 'active' ? 'Block' : 'Unblock'}
                  </Button>
                </BlockConfirmDialog>
              </ReadPermissionGate>

              {referralStack.length >= 1 && (
                <Button
                  onClick={() => {
                    setCurrentUser(currentReferral);
                    goBackReferral();
                  }}
                >
                  Back
                </Button>
              )}
            </div>
            Client {currentUser.coinsFlyId} details
            <div className="absolute right-4 top-0 flex gap-2">
              {tabValue === 'tab4' && (
                <Button
                  disabled={
                    !canUpdate(PERMISSION_RESOURCES.CLIENTS_LIST) ||
                    !canUpdate(PERMISSION_RESOURCES.CLIENT_PROFILE) ||
                    !canUpdate(PERMISSION_RESOURCES.LOGS) ||
                    !canUpdate(PERMISSION_RESOURCES.CLIENT_PROFILE)
                  }
                  onClick={handleExportCsv}
                >
                  Export CSV
                </Button>
              )}
              {canRead(PERMISSION_RESOURCES.KYC_CTA) && (
                <Button
                  className="justify-center"
                  disabled={
                    !canUpdate(PERMISSION_RESOURCES.CLIENTS_LIST) ||
                    !canUpdate(PERMISSION_RESOURCES.CLIENT_PROFILE) ||
                    !canRead(PERMISSION_RESOURCES.KYC_CTA) ||
                    !canUpdate(PERMISSION_RESOURCES.KYC_CTA)
                  }
                >
                  <Link
                    target="_blank"
                    to={
                      currentUser.kycVerificationId
                        ? `https://kyc-app.amlbot.com/verifications/${currentUser.kycVerificationId}`
                        : currentUser.kycApplicantId
                          ? `https://kyc-app.amlbot.com/applicants/${currentUser.kycApplicantId}/overview`
                          : 'https://kyc-app.amlbot.com/dashboard'
                    }
                    onClick={handleKYCClick}
                  >
                    KYC
                  </Link>
                </Button>
              )}
            </div>
          </Dialog.Title>
          <Card extra={'w-full sm:overflow-auto lg-max:px-1 px-6 pb-6'}>
            <Tabs.Root
              value={tabValue}
              onValueChange={setTabValue}
              className="relative mt-8 grow overflow-x-auto"
            >
              <Tabs.List className="flex justify-center gap-5">
                <ReadPermissionGate resource={PERMISSION_RESOURCES.INFO}>
                  <Tabs.Trigger
                    value="tab1"
                    className="px-4 py-2 text-[16px] font-medium text-gray-700 border-b-2 border-transparent data-[state=active]:border-brand-500"
                  >
                    Info
                  </Tabs.Trigger>
                </ReadPermissionGate>
                <ReadPermissionGate
                  resource={PERMISSION_RESOURCES.TRANSACTIONS}
                >
                  <Tabs.Trigger
                    value="tab2"
                    className="px-4 py-2 text-[16px] font-medium text-gray-700 border-b-2 border-transparent data-[state=active]:border-brand-500"
                  >
                    Transactions
                  </Tabs.Trigger>
                </ReadPermissionGate>
                <ReadPermissionGate resource={PERMISSION_RESOURCES.REFERRALS}>
                  <Tabs.Trigger
                    value="tab3"
                    className="px-4 py-2 text-[16px] font-medium text-gray-700 border-b-2 border-transparent data-[state=active]:border-brand-500"
                  >
                    Referrals
                  </Tabs.Trigger>
                </ReadPermissionGate>
                <ReadPermissionGate resource={PERMISSION_RESOURCES.LOGS}>
                  <Tabs.Trigger
                    value="tab4"
                    className="px-4 py-2 text-[16px] font-medium text-gray-700 border-b-2 border-transparent data-[state=active]:border-brand-500"
                  >
                    Logs
                  </Tabs.Trigger>
                </ReadPermissionGate>
                <ReadPermissionGate resource={PERMISSION_RESOURCES.CARDS}>
                  <Tabs.Trigger
                    value="tab5"
                    className="px-4 py-2 text-[16px] font-medium text-gray-700 border-b-2 border-transparent data-[state=active]:border-brand-500"
                  >
                    Cards
                  </Tabs.Trigger>
                </ReadPermissionGate>

                <ReadPermissionGate resource={PERMISSION_RESOURCES.WALLETS}>
                  <Tabs.Trigger
                    value="tab6"
                    className="px-4 py-2 text-[16px] font-medium text-gray-700 border-b-2 border-transparent data-[state=active]:border-brand-500"
                  >
                    Wallets
                  </Tabs.Trigger>
                </ReadPermissionGate>
              </Tabs.List>
              <div className=" overflow-y-auto">
                <ReadPermissionGate resource={PERMISSION_RESOURCES.INFO}>
                  <Tabs.Content
                    value="tab1"
                    className="lg-max:p-1 lg-max:pt-3 p-4"
                  >
                    <ul className="flex flex-col gap-3">
                      <li>
                        Client id:{' '}
                        <span className="font-semibold ml-3">
                          {currentUser.coinsFlyId}
                        </span>
                      </li>
                      <li>
                        Telegram username:{' '}
                        <span className="font-semibold ml-3">
                          {currentUser.username}
                        </span>
                      </li>
                      <li>
                        Email:{' '}
                        <span className="font-semibold ml-3">
                          {currentUser.email ?? '---'}
                        </span>
                      </li>
                      <li>
                        Registration date:{' '}
                        <span className="font-semibold ml-3">
                          {formatDate(
                            currentUser.createdAt,
                            'DD.MM.YYYY HH:mm'
                          )}
                        </span>
                      </li>
                      <li>
                        Individual Verification (KYC):{' '}
                        <span className="font-semibold ml-3">
                          {currentUser.kycStatus
                            ? '✅ Verified'
                            : 'Not Verified'}
                        </span>
                      </li>
                      <li>
                        2FA Status:
                        <span className="relative font-semibold ml-3">
                          {currentUser.toFAStatus ? 'Enabled' : 'Disabled'}

                          {currentUser.toFAStatus ? (
                            <Disable2FaDialog
                              user={currentUser}
                              refetchUsers={refetchUsers}
                            >
                              <Button
                                disabled={
                                  !canUpdate(
                                    PERMISSION_RESOURCES.CLIENTS_LIST
                                  ) ||
                                  !canUpdate(
                                    PERMISSION_RESOURCES.CLIENT_PROFILE
                                  ) ||
                                  !canUpdate(PERMISSION_RESOURCES.INFO)
                                }
                                className={clsx(
                                  'absolute left-[110%] -top-[12px] justify-center sm-max:mt-2',
                                  'bg-red-600 hover:bg-red-700'
                                )}
                              >
                                Disable
                              </Button>
                            </Disable2FaDialog>
                          ) : null}
                        </span>
                      </li>
                      <KybSection
                        user={currentUser}
                        onChanged={() => {
                          refetchUsers();
                          refreshUser();
                        }}
                      />
                      <li>
                        Referral Count:{' '}
                        <span className="font-semibold ml-3">
                          {currentUser.referralCount}
                        </span>
                      </li>
                      <li>
                        Commissions Earned:{' '}
                        <span className="font-semibold ml-3">
                          {isRewardsLoading && 'Loading...'}
                          {!isRewardsLoading && (
                            <>
                              {referralReward?.totalReferralRewards || 0} USDT
                            </>
                          )}
                        </span>
                      </li>
                      <li>
                        Last Login Date:{' '}
                        <span className="font-semibold ml-3">
                          {formatDate(
                            currentUser.lastActiveAt,
                            'DD.MM.YYYY HH:mm'
                          )}
                        </span>
                      </li>
                      <li>
                        User Activity Status:{' '}
                        <span className="font-semibold ml-3">
                          {currentUser.userStatus?.charAt(0).toUpperCase() +
                            currentUser.userStatus?.slice(1)}
                        </span>
                      </li>
                      <li>
                        Language:{' '}
                        <span className="font-semibold ml-3">
                          {currentUser.userLanguage?.toUpperCase()}
                        </span>
                      </li>
                      <li className="flex flex-col gap-2">
                        <div>
                          <span>Wallet Balances: </span>
                          <span className="font-semibold">
                            {currentUser.totalInUsdt > 0 ? (
                              <span>
                                {formatNumber(currentUser.totalInUsdt)} USDT
                              </span>
                            ) : (
                              0
                            )}{' '}
                          </span>
                        </div>
                        {isOpen && (
                          <UserBalances
                            user={currentUser}
                            onChanged={() => {
                              refetchUsers();
                              refreshUser();
                            }}
                          />
                        )}
                      </li>
                    </ul>
                  </Tabs.Content>
                </ReadPermissionGate>
                <ReadPermissionGate
                  resource={PERMISSION_RESOURCES.TRANSACTIONS}
                >
                  <Tabs.Content
                    value="tab2"
                    className="lg-max:p-1 lg-max:pt-3 p-4"
                  >
                    {currentView === 'userDetails' ? (
                      <UserTxsTable
                        user={currentUser}
                        onTransactionClick={handleTransactionClick}
                      />
                    ) : selectedTransaction ? (
                      <ReadPermissionGate
                        resource={PERMISSION_RESOURCES.TRANSACTION_INNER_PAGE}
                        fallback={
                          <div className="flex flex-col items-center justify-center h-64 text-center">
                            <div className="text-gray-500 dark:text-gray-400 mb-4">
                              You don't have permission to view transaction
                              details
                            </div>
                            <Button
                              onClick={() => setCurrentView('userDetails')}
                              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                            >
                              Back to User Details
                            </Button>
                          </div>
                        }
                      >
                        <TransactionDetails
                          {...getTransactionProps(selectedTransaction)}
                        />
                      </ReadPermissionGate>
                    ) : (
                      <div>No transaction selected</div>
                    )}
                  </Tabs.Content>
                </ReadPermissionGate>
                <ReadPermissionGate resource={PERMISSION_RESOURCES.REFERRALS}>
                  <Tabs.Content
                    value="tab3"
                    className="lg-max:p-1 lg-max:pt-3 p-4"
                  >
                    <UserReferrals
                      user={currentUser}
                      onUserClick={async userId => {
                        try {
                          const userData = await usersService.getUser(userId);

                          if (userData) {
                            setCurrentUser(userData);
                            setTabValue('tab1');
                          } else {
                            console.error('User not found for userId:', userId);
                          }
                        } catch (error) {
                          console.error('Error fetching user data:', error);
                        }
                      }}
                    />
                  </Tabs.Content>
                </ReadPermissionGate>
                <ReadPermissionGate resource={PERMISSION_RESOURCES.LOGS}>
                  <Tabs.Content
                    value="tab4"
                    className="lg-max:p-1 lg-max:pt-3 p-4"
                  >
                    <UserLogs user={currentUser} />
                  </Tabs.Content>
                </ReadPermissionGate>
                <ReadPermissionGate resource={PERMISSION_RESOURCES.CARDS}>
                  <Tabs.Content
                    value="tab5"
                    className="lg-max:p-1 lg-max:pt-3 p-4"
                  >
                    <UserCardsTab user={currentUser} />
                  </Tabs.Content>
                </ReadPermissionGate>
                <ReadPermissionGate resource={PERMISSION_RESOURCES.WALLETS}>
                  <Tabs.Content
                    value="tab6"
                    className="lg-max:p-1 lg-max:pt-3 p-4"
                  >
                    <UserWallets user={currentUser} />
                  </Tabs.Content>
                </ReadPermissionGate>
              </div>
            </Tabs.Root>
          </Card>
          <Dialog.Close asChild>
            <button
              type="button"
              className="text-violet11 hover:bg-violet4 focus:shadow-violet7 absolute right-[10px] top-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full focus:shadow-[0_0_0_2px] focus:outline-none"
              aria-label="Close"
            >
              <MdClose />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default UserHistoryDialog;
